import type { CompRhythmChord, CompRhythmOptions, PlannedAttack } from './compRhythm';

interface GrooveAttack {
  beat: number;
  duration: number;
  velocity: number;
  amount: number;
}

// Original two-bar practice groove, not a transcription. A repeating backbone
// makes the syncopation intelligible; activity adds replies without moving it.
// Durations are part of the figure: a supported downbeat and clipped offbeats.
const GROOVE: readonly GrooveAttack[] = [
  { beat: 0, duration: 1.25, velocity: 0.86, amount: 0 },
  { beat: 0.75, duration: 0.22, velocity: 0.58, amount: 0.85 },
  { beat: 1.75, duration: 0.3, velocity: 0.68, amount: 0.35 },
  { beat: 2.5, duration: 0.55, velocity: 0.78, amount: 0 },
  { beat: 4, duration: 1, velocity: 0.82, amount: 0 },
  { beat: 5.75, duration: 0.3, velocity: 0.62, amount: 0.7 },
  { beat: 6.5, duration: 0.55, velocity: 0.76, amount: 0 },
  { beat: 7.5, duration: 0.4, velocity: 0.65, amount: 0.5 },
];

/** Straight-sixteenth funk. The bass/drums own the pulse; comping repeats a
 * two-bar figure instead of making unrelated anticipations at every change.
 * Extra replies yield to dense written melody; the backbone stays recognisable.
 */
export function planFunkCompRhythm(
  chords: CompRhythmChord[],
  opts: CompRhythmOptions,
): PlannedAttack[] {
  if (!chords.length) return [];
  const origin = opts.barOffset ?? 0;
  const amount = Math.max(0, Math.min(1, opts.amount ?? 0.5));
  const end = Math.max(...chords.map((chord) => chord.startBeat + chord.durationBeats));
  const first = Math.max(origin, chords[0].startBeat);
  const onsets = [...new Set((opts.melody ?? []).map((note) => note.beat))];
  const busy = (beat: number) =>
    onsets.filter((onset) => onset >= beat - 0.5 && onset < beat + 0.5).length >= 2;
  const sounds = (beat: number) =>
    chords.some((chord) => chord.startBeat <= beat && beat < chord.startBeat + chord.durationBeats);
  const result: PlannedAttack[] = [];
  for (let start = origin + Math.floor((first - origin) / 8) * 8; start < end; start += 8) {
    for (const hit of GROOVE) {
      const beat = start + hit.beat;
      if (hit.amount > amount || beat < first || beat >= end || !sounds(beat)) continue;
      if (hit.amount > 0 && busy(beat)) continue;
      result.push({
        beat,
        durationBeats: Math.min(hit.duration, end - beat),
        velocity: hit.velocity,
        rollSec: 0.002,
      });
    }
  }
  return result.map((hit, index) => ({
    ...hit,
    durationBeats: Math.min(hit.durationBeats, (result[index + 1]?.beat ?? end) - hit.beat),
  }));
}
